import {Options, TableColumn} from '@fl/cmsch-fe-library';

import {CorrectionReport} from 'api/gen/CorrectionReport';
import {CorrectionType} from 'api/gen/CorrectionType';

import {TFunction} from '../../../translations';

/**
 * Creates correction-type options for APC selects and table filters.
 *
 * @param t Translation helper for the APC namespace.
 * @returns Correction-type options used by APC UI components.
 */
export const correctionTypeOptions = (t: TFunction<'analysisProtocolCorrection'>): Options<CorrectionType> => [
    {label: t('correctionTypeDiscard'), value: 'DISCARD'},
    {label: t('correctionTypeUpdate'), value: 'UPDATE'},
    {label: t('correctionTypeUpdateEarTag'), value: 'EAR_TAG_UPDATE'},
];

/**
 * Creates the APC overview table column configuration.
 *
 * @param t Translation helper for the APC namespace.
 * @returns Column definitions for the APC overview table.
 */
// eslint-disable-next-line max-lines-per-function
export const getColumns = (t: TFunction<'analysisProtocolCorrection'>): Array<TableColumn<CorrectionReport>> => [
    {
        field: 'proficiencyTestDate',
        type: 'date',
        dateFormat: 'date',
        sortable: true,
    },
    {
        field: 'earTag',
        type: 'string',
        filterTypes: [
            'betweenEarTags',
            'containsStringOr',
            {type: 'multiSelectStringOrEquals', subtype: 'bulkString'},
            'null',
        ],
        sortable: true,
    },
    {
        field: 'updatedEarTag',
        type: 'string',
        customFiltersOnly: true,
        filterTypes: ['containsString', 'betweenEarTags', 'null'],
        sortable: true,
    },
    {
        field: 'correctionType',
        type: 'option',
        options: correctionTypeOptions(t),
        sortable: true,
    },
    {
        field: 'breederName',
        type: 'string',
        sortable: true,
        filterTypes: ['containsStringOr', {type: 'multiSelectStringOrEquals', subtype: 'bulkString'}, 'null'],
    },
    {
        field: 'stableCode',
        type: 'string',
        sortable: true,
        filterTypes: ['containsStringOr', {type: 'multiSelectStringOrEquals', subtype: 'bulkString'}, 'null'],
    },
    {
        field: 'stableName',
        type: 'string',
        sortable: true,
        filterTypes: ['containsStringOr', {type: 'multiSelectStringOrEquals', subtype: 'bulkString'}, 'null'],
    },
    {
        field: 'organizationName',
        type: 'string',
        sortable: true,
        filterTypes: ['containsStringOr', {type: 'multiSelectStringOrEquals', subtype: 'bulkString'}, 'null'],
    },
    {
        field: 'technicianNumber',
        type: 'string',
        sortable: true,
        filterTypes: ['containsStringOr', {type: 'multiSelectStringOrEquals', subtype: 'bulkString'}, 'null'],
    },
    {
        field: 'lactationOrder',
        type: 'number',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'calveDate',
        type: 'date',
        dateFormat: 'date',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'milkingDayTime',
        type: 'number',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'milkingInterval',
        type: 'number',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'milkingCount',
        type: 'number',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'milkKg',
        type: 'decimal',
        fixedDecimals: 2,
        sortable: true,
    },
    {
        field: 'fatPercentage',
        type: 'decimal',
        fixedDecimals: 2,
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'proteinPercentage',
        type: 'decimal',
        fixedDecimals: 2,
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'lactosePercentage',
        type: 'decimal',
        fixedDecimals: 2,
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'somaticCells',
        type: 'decimal',
        fixedDecimals: 0,
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'urea',
        type: 'decimal',
        fixedDecimals: 2,
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'methodOfProficiencyTest',
        type: 'number',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'currentMethodOfProficiencyTest',
        type: 'number',
        sortable: true,
        filterTypes: ['null'],
    },
    {
        field: 'methodOfProficiencyTestChanged',
        type: 'optionalBoolean',
        sortable: true,
    },
    {
        field: 'lastChangedBy',
        type: 'string',
        sortable: true,
        filterTypes: ['containsStringOr', {type: 'multiSelectStringOrEquals', subtype: 'bulkString'}, 'null'],
    },
    {
        field: 'updated',
        type: 'date',
        dateFormat: 'datetime',
        sortable: true,
        filterTypes: ['null'],
    },
];
